import { inject, Injectable } from '@angular/core';
import { PluginType, GameData } from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
	GenerationStep,
} from '../game-generation/base/game-generation.interface';
import { GameGenerationRegistryService } from '../game-generation/game-generation-registry.service';
import { SettingsService } from '../settings-service/settings.service';
import { SourceMaterialService } from '../../source-material-service/source-material.service';

/**
 * Default generation settings for each plugin type
 */
export interface DefaultGenerationSettings {
	multiChoice: {
		questionCount: number;
		answerCount: number;
		clearPrevious: boolean;
	};
	detectiveCaseStudy: {
		caseTitle: string;
		clearPrevious: boolean;
		solutionClueCount: number;
	};
	digest: {
		numberOfTopics: number;
		clearPrevious: boolean;
	};
	crosswordPuzzle: {
		generationType: string;
		maxSuggestions: number;
		maxSecondaryWords: number;
		clearPrevious: boolean;
	};
	// Add other plugin defaults as needed
}

// Export interfaces from base for backward compatibility
export type {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from '../game-generation/base/game-generation.interface';

/**
 * Core generation utilities service
 */
@Injectable({
	providedIn: 'root',
})
export class GenerationUtils {
	sourceMaterialService = inject(SourceMaterialService);

	constructor(
		private gameGenerationRegistry: GameGenerationRegistryService,
	) {}

	/**
	 * Create prompts for a specific game type
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const service = this.gameGenerationRegistry.getService(
			request.gameType,
		);

		// Add images as string to be able to use them in generation.
		if (request.sourceMaterial) {
			request.sourceMaterial.imagesAsString =
				this.sourceMaterialService.GetImagesAsString(
					request.sourceMaterial?.selectedArticles,
				) || '';
		}

		const basePrompts = service.createPrompts(request, settingsService);

		// Enhance user prompt with concept analysis if items are selected
		const enhancedUserPrompt = service.enhanceUserPrompt
			? service.enhanceUserPrompt(
					basePrompts.user,
					request.parameters.selectedConceptItems,
					request.parameters['materialHandlingInstructions'],
				)
			: basePrompts.user;

		return {
			system: basePrompts.system,
			user: enhancedUserPrompt,
			// Forward multipleRequests if present (for multi-request generation)
			multipleRequests: basePrompts.multipleRequests,
		};
	}

	/**
	 * Process AI response into game data for a specific game type
	 * @param gameType The type of game being generated
	 * @param response The AI response object
	 * @param parameters Original generation parameters
	 * @returns Processed game data result
	 */
	processResponse(
		gameType: PluginType,
		response: any,
		parameters: any,
	): GenerationResult {
		const service = this.gameGenerationRegistry.getService(gameType);
		return service.processResponse(response, parameters);
	}

	/**
	 * Get default parameters for a specific game type
	 * @param gameType The type of game
	 * @returns Default parameters for the game type
	 */
	getDefaultParameters(gameType: PluginType): any {
		const service = this.gameGenerationRegistry.getService(gameType);
		return service.getDefaultParameters();
	}

	/**
	 * Get default steps for a specific game type
	 * Delegates to the game-specific generation service
	 * @param gameType The type of game
	 * @returns Array of generation steps
	 */
	getDefaultSteps(gameType: PluginType): GenerationStep[] {
		const service = this.gameGenerationRegistry.getService(gameType);

		// If service implements getDefaultSteps, use it
		if (service.getDefaultSteps) {
			return service.getDefaultSteps();
		}

		// Fallback: wrap getDefaultParameters in single step
		return [
			{
				stepId: 'main',
				stepType: 'generate',
				parameters: service.getDefaultParameters(),
			},
		];
	}

	/**
	 * Get default background steps for a specific game type.
	 * Background steps are optimized for batch processing.
	 * Delegates to the game-specific generation service.
	 * @param gameType The type of game
	 * @returns Array of generation steps for background processing
	 */
	getDefaultBackgroundSteps(gameType: PluginType): GenerationStep[] {
		const service = this.gameGenerationRegistry.getService(gameType);

		// If service implements getDefaultBackgroundSteps, use it
		if (service.getDefaultBackgroundSteps) {
			return service.getDefaultBackgroundSteps();
		}

		// Fallback: use regular steps
		return this.getDefaultSteps(gameType);
	}

	/**
	 * Create a generation request with steps for foreground generation.
	 * This is the single source of truth for foreground request creation.
	 * @param gameType The type of game to generate
	 * @param user User information
	 * @param parameters Generation parameters
	 * @param sourceMaterial Optional source material
	 * @returns Complete generation request with steps
	 */
	createRequest(
		gameType: PluginType,
		user: { id: string; email: string },
		parameters: Record<string, unknown>,
		sourceMaterial?: {
			selectedArticles: unknown[];
			fileHandles: string[];
			imagesAsString: string;
		},
	): GenerationRequest {
		const steps = this.getDefaultSteps(gameType);

		// Merge provided parameters with the first step's parameters
		if (steps.length > 0) {
			steps[0].parameters = {
				...steps[0].parameters,
				...parameters,
			};
		}

		return {
			gameType,
			user,
			steps,
			parameters: parameters,
			sourceMaterial,
		};
	}

	/**
	 * Create a generation request with steps for background generation.
	 * This is the single source of truth for background request creation.
	 * @param gameType The type of game to generate
	 * @param user User information
	 * @param parameters Generation parameters
	 * @param sourceMaterial Optional source material
	 * @returns Complete generation request with steps optimized for background processing
	 */
	createBackgroundRequest(
		gameType: PluginType,
		user: { id: string; email: string },
		parameters: Record<string, unknown>,
		sourceMaterial?: {
			selectedArticles: unknown[];
			fileHandles: string[];
			imagesAsString: string;
		},
	): GenerationRequest {
		// Mark parameters as background request
		const backgroundParameters = {
			...parameters,
			isBackgroundRequest: true,
		};

		const steps = this.getDefaultBackgroundSteps(gameType);

		// Merge provided parameters with the first step's parameters
		if (steps.length > 0) {
			steps[0].parameters = {
				...steps[0].parameters,
				...backgroundParameters,
			};
		}

		return {
			gameType,
			user,
			steps,
			parameters: backgroundParameters, // Include parameters at request level for batch service
			sourceMaterial,
		};
	}

	/**
	 * Validate generation request
	 * @param request The generation request to validate
	 * @returns Validation result with error message if invalid
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		if (!request.user) {
			return { valid: false, error: 'User is required' };
		}

		if (!request.gameType) {
			return { valid: false, error: 'Game type is required' };
		}

		// Check if service is registered for this game type
		if (!this.gameGenerationRegistry.hasService(request.gameType)) {
			return {
				valid: false,
				error: `Unsupported game type: ${request.gameType}`,
			};
		}

		// Delegate to plugin-specific validation
		const service = this.gameGenerationRegistry.getService(
			request.gameType,
		);
		return service.validateRequest(request);
	}

	/**
	 * Merge new content with existing game data based on parameters
	 * @param gameType The type of game
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	mergeContent(
		gameType: PluginType,
		existingGameData: GameData,
		newGameData: GameData,
		clearPrevious: boolean,
	): GameData {
		const service = this.gameGenerationRegistry.getService(gameType);
		if (service.mergeContent) {
			return service.mergeContent(
				existingGameData,
				newGameData,
				clearPrevious,
			);
		}

		// Fallback: replace existing with new if clearPrevious, otherwise return new
		return clearPrevious ? newGameData : existingGameData;
	}
}
